import { useState, useEffect, useRef, useCallback } from 'react';
import { Hospital } from 'lucide-react';
// StepperNavigation kept in case of future local footer; current footer uses SelectionFooter
import { RequestMethodsGroup } from './RequestMethodsGroup';
import { SelectionFooter } from './SelectionFooter';

import { RecommendationsAccordion } from './RecommendationsAccordion';
import { useGetOrgProfileById } from '@/feature/profiles/hooks/profilesAPI';
import { usePatientRequestContext } from '../../../context/usePatientRequestContext';
import { RetrievalMethod } from '@/feature/profiles/constants';
import { RequestLoading } from './RequestLoading';
import { useGetOrganizationInfo } from '@/feature/profiles/hooks/organizationAPI';
import { getAvailableRequestActions } from '@/feature/profiles/utils/requestActions';
import { useCanCreateStandaloneFacesheetRequest } from '@/feature/patients/hooks/patientsAPI';
import {
  deriveFacesheetTemplateCapabilities,
  validateFacesheetsSelection,
} from './facesheetsRules';
import {
  filterDisabledRequestMethods,
  getHiePostalCodeDisabledReason,
  getRequestMethodDisabledState,
} from './requestSelectionUtils';

interface RequestActionsSelectionProps {
  onNext: () => void;
  onBackToMainStep: () => void;
  disablePrevious?: boolean;
}

type MethodErrorState = Record<
  string,
  {
    message: string;
    grouped?: boolean;
  }
>;

export const RequestSelection = ({
  onNext,
  onBackToMainStep,
  disablePrevious = false,
}: RequestActionsSelectionProps) => {
  const {
    requestState,
    setRequestState,
    isLoading: isLoadingPatientRequest,
    patientId,
    isEditMode,
  } = usePatientRequestContext();
  const selectedRequestMethods =
    requestState.request?.selectedRequestMethods ?? [];
  const isFacesheetsSelected = selectedRequestMethods.includes(
    RetrievalMethod.FACESHEETS,
  );
  const isHieSelected = selectedRequestMethods.includes(RetrievalMethod.HIE);
  const patientData = requestState.patient;
  const standaloneFacesheetPatientId =
    !isEditMode && !isLoadingPatientRequest
      ? patientId || requestState.patient?.id
      : undefined;

  const [showRecordsError, setShowRecordsError] = useState(false);
  const [recordsErrorMessage, setRecordsErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [recordsMethodErrors, setRecordsMethodErrors] =
    useState<MethodErrorState>({});
  const [footerErrorMessage, setFooterErrorMessage] = useState<
    string | undefined
  >(undefined);

  const { data: profile, isLoading } = useGetOrgProfileById(
    requestState.organizationProfileId,
  );

  const { data: organizationInfo } = useGetOrganizationInfo();
  const isAdvanced =
    organizationInfo?.enableAdvancedFacesheetsRequests === true;
  const hasFacesheetTemplates = Boolean(profile?.facesheets.length);
  const { data: standaloneFacesheetAccess } =
    useCanCreateStandaloneFacesheetRequest(standaloneFacesheetPatientId);

  const hiePostalCodeDisabledReason = getHiePostalCodeDisabledReason({
    postalCode: patientData?.address?.postalCode,
  });

  const getMethodDisabledState = useCallback(
    (method: string) =>
      getRequestMethodDisabledState({
        method,
        hasProfileNpi: Boolean(profile?.npi),
        hasFacesheetTemplates,
        hiePostalCodeDisabledReason,
      }),
    [hasFacesheetTemplates, hiePostalCodeDisabledReason, profile?.npi],
  );

  const defaultsAppliedRef = useRef(false);

  // default selected methods and facesheets based on profile — runs once
  useEffect(() => {
    if (defaultsAppliedRef.current || !profile) return;

    const defaultFacesheets =
      profile.facesheets.filter(
        (facesheet) => facesheet.isDefault && !facesheet.isExpedited,
      ) ?? [];

    if (selectedRequestMethods.length === 0) {
      const defaultMethods = filterDisabledRequestMethods(
        profile.defaultRetrievalMethods as RetrievalMethod[],
        (method) => getMethodDisabledState(method).disabled,
      );

      setRequestState((prev) => ({
        ...prev,
        request: {
          ...prev.request,
          selectedRequestMethods: defaultMethods,
          actionFormsData: {
            ...prev.request.actionFormsData,
            [RetrievalMethod.FACESHEETS]: {
              ...(prev.request.actionFormsData?.[RetrievalMethod.FACESHEETS] ??
                {}),
              selectedFacesheets: defaultFacesheets,
            },
          },
        },
      }));
    }

    defaultsAppliedRef.current = true;
  }, [
    getMethodDisabledState,
    profile,
    selectedRequestMethods.length,
    setRequestState,
  ]);

  useEffect(() => {
    if (!profile || profile.facesheets.length > 0 || !isFacesheetsSelected) {
      return;
    }

    setRequestState((prev) => ({
      ...prev,
      request: {
        ...prev.request,
        selectedRequestMethods: prev.request.selectedRequestMethods.filter(
          (method) => method !== RetrievalMethod.FACESHEETS,
        ),
        actionFormsData: {
          ...prev.request.actionFormsData,
          [RetrievalMethod.FACESHEETS]: {
            ...prev.request.actionFormsData[RetrievalMethod.FACESHEETS],
            selectedFacesheets: [],
          },
        },
      },
    }));
  }, [isFacesheetsSelected, profile, setRequestState]);

  useEffect(() => {
    if (!hiePostalCodeDisabledReason || !isHieSelected) {
      return;
    }

    setRequestState((prev) => ({
      ...prev,
      request: {
        ...prev.request,
        selectedRequestMethods: prev.request.selectedRequestMethods.filter(
          (method) => method !== RetrievalMethod.HIE,
        ),
      },
    }));
  }, [hiePostalCodeDisabledReason, isHieSelected, setRequestState]);

  if (isLoading || isLoadingPatientRequest) {
    return <RequestLoading />;
  }

  if (!profile) {
    return <div>Profile not found</div>;
  }

  // Get available request actions based on organization enablements and profile available methods
  const availableActions = getAvailableRequestActions({
    orgInfo: organizationInfo,
    organizationFacesheets: profile.facesheets,
    profileAvailableMethods: profile.availableRetrievalMethods,
    profileFacesheets: profile.facesheets,
  });

  // Filter methods by category using available actions
  const recordsAndDataMethods = availableActions
    .filter((action) => action.category === 'Records & Data')
    .map((action) => action.name);

  const analysisMethods = availableActions
    .filter((action) => action.category === 'Analysis')
    .map((action) => action.name);
  const facesheetTemplateCapabilities = deriveFacesheetTemplateCapabilities(
    profile.facesheets,
  );
  const toggleMethod = (method: string) => {
    if (getMethodDisabledState(method).disabled) return;

    setShowRecordsError(false);
    setRecordsErrorMessage(undefined);
    setRecordsMethodErrors({});
    setFooterErrorMessage(undefined);
    setRequestState((prev) => ({
      ...prev,
      request: {
        ...prev.request,
        selectedRequestMethods: selectedRequestMethods.includes(
          method as RetrievalMethod,
        )
          ? selectedRequestMethods.filter((m) => m !== method)
          : [...selectedRequestMethods, method as RetrievalMethod],
      },
    }));
  };

  const handleNextClick = () => {
    setRecordsMethodErrors({});
    setShowRecordsError(false);
    setRecordsErrorMessage(undefined);
    setFooterErrorMessage(undefined);

    const isNoneSelected = selectedRequestMethods.length === 0;

    if (isNoneSelected) {
      setFooterErrorMessage('Please select at least one product or service.');
      return;
    }

    const facesheetsValidation = validateFacesheetsSelection({
      selectedMethods: selectedRequestMethods,
      capabilities: facesheetTemplateCapabilities,
      hasFacesheetTemplates,
      allowStandaloneFacesheets: standaloneFacesheetAccess?.canCreate === true,
      isAdvanced,
    });
    if (!facesheetsValidation.isValid && facesheetsValidation.errorMessage) {
      const missingMethods = facesheetsValidation.missingMethods ?? [];
      const nextMethodErrors: MethodErrorState = {};
      const isHieRequiresFacesheetsError =
        missingMethods.length === 1 &&
        missingMethods.includes(RetrievalMethod.FACESHEETS);
      const shouldGroupMissingMethods =
        !isHieRequiresFacesheetsError && missingMethods.length > 1;

      if (missingMethods.length > 0) {
        missingMethods.forEach((method) => {
          nextMethodErrors[method] = {
            message: facesheetsValidation.errorMessage!,
            grouped: shouldGroupMissingMethods,
          };
        });
      } else {
        nextMethodErrors[RetrievalMethod.FACESHEETS] = {
          message: facesheetsValidation.errorMessage,
          grouped: false,
        };
      }

      setShowRecordsError(!isHieRequiresFacesheetsError);
      setRecordsErrorMessage(
        isHieRequiresFacesheetsError
          ? undefined
          : facesheetsValidation.errorMessage,
      );
      setRecordsMethodErrors(nextMethodErrors);
      setFooterErrorMessage(
        isHieRequiresFacesheetsError
          ? facesheetsValidation.errorMessage
          : 'Please fix validation errors.',
      );
      return;
    }

    onNext();
  };

  return (
    <div className="flex flex-col justify-center gap-6 self-stretch">
      <div className="m-auto flex flex-col items-center gap-4 px-8 pt-5">
        <div className="text-2xl">
          What do you need for {patientData?.firstName} {patientData?.lastName}?
        </div>
        <div className="flex gap-2">
          <Hospital />
          <span>{profile.displayName}</span>
        </div>
      </div>
      <div className="m-auto flex w-[60%] flex-col gap-6">
        <RequestMethodsGroup
          title="Records & Data"
          methods={recordsAndDataMethods}
          selectedMethods={selectedRequestMethods}
          onToggle={toggleMethod}
          getDisabledState={getMethodDisabledState}
          showError={showRecordsError}
          errorMessage={recordsErrorMessage}
          methodErrors={recordsMethodErrors}
        />

        <RequestMethodsGroup
          title="Analysis"
          methods={analysisMethods}
          selectedMethods={selectedRequestMethods}
          onToggle={toggleMethod}
          methodErrors={recordsMethodErrors}
          getDisabledState={getMethodDisabledState}
        />

        <SelectionFooter
          onPrevious={onBackToMainStep}
          onNext={handleNextClick}
          showValidationError={Boolean(footerErrorMessage)}
          disablePrevious={disablePrevious}
          errorMessage={footerErrorMessage}
        />

        <RecommendationsAccordion
          methodsPresent={[...recordsAndDataMethods, ...analysisMethods]}
        />
      </div>
    </div>
  );
};
